import React from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { TrashIcon } from 'lucide-react';
import { PermissionGuard } from '@/features/auth';
import { Badge, Button, LoadingSpinner, Table } from '@/shared/components';
import type { DealerBankAccount } from '../types/dealers.types';

interface Props {
    data: DealerBankAccount[];
    isLoading?: boolean;
    onDelete: (id: string) => void;
    isDeleting?: boolean;
    emptyMessage?: string;
}

export const DealerBankAccountDataTable: React.FC<Props> = ({
    data,
    isLoading = false,
    onDelete,
    isDeleting = false,
    emptyMessage,
}) => {
    if (isLoading) {
        return <LoadingSpinner size="xl" className="py-12" message="Loading..." />;
    }

    const columns: ColumnDef<DealerBankAccount>[] = [
        {
            accessorKey: 'account_name',
            header: 'Account Name',
            cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'account_number_last4',
            header: 'Account Number',
            cell: (info) => <span className="font-mono text-sm">****{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'sort_code_last2',
            header: 'Sort Code',
            cell: (info) => <span className="font-mono text-sm">**-**-{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'bank_name',
            header: 'Bank',
            cell: (info) => <span className="text-sm">{(info.getValue() as string | null) ?? <span className="text-mp-muted">—</span>}</span>,
        },
        {
            accessorKey: 'is_primary',
            header: 'Primary',
            cell: (info) => info.getValue()
                ? <Badge size="xs" variant="success">Primary</Badge>
                : null,
        },
        {
            id: 'actions',
            header: '',
            cell: (info) => (
                <PermissionGuard permission={{ resource: 'dealer', action: 'edit' }}>
                    <Button
                        size="none"
                        variant="clear"
                        onClick={() => onDelete(info.row.original.id)}
                        disabled={isDeleting}
                        startIcon={<TrashIcon size={18} className="text-mp-error" />}
                    />
                </PermissionGuard>
            ),
            enableSorting: false,
        },
    ];

    return <Table<DealerBankAccount> data={data} columns={columns} enableSorting={false} emptyMessage={emptyMessage} />;
};
